import { BusinessEntity } from '@/@types/generated'
import { HasFeature } from '@/components/Atoms/Permissions'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@/components/ui/navigation-menu'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { useAuth } from '@/hooks/useAuth'
import { CertificatesFeature } from '@/lib/features'
import { CompaniesFeature } from '@/lib/features/companies'
import { Feature } from '@/lib/features/feature'
import { InsuranceFeature } from '@/lib/features/insurance'
import { cn } from '@/lib/utils'
import { buildPath, RouteKey } from '@/routes'
import { isBrokerCompany } from '../lib/is-broker-company'
import { useCarrierCargoTypes } from '@/hooks/useCarrierCargoTypes'
import { PropsWithChildren } from 'react'
import { NavLink as ReactRouterNavLink, To } from 'react-router'

interface CompanyDetailsNavigationProps {
  company: BusinessEntity
}

interface NavigationItem {
  label: string
  to: RouteKey
  feature?: Feature
}

export const CompanyDetailsNavigationItems = [
  {
    label: 'Certificate',
    to: '/companies/:businessEntityId/certificate',
    feature: CertificatesFeature.View,
  },
  {
    label: 'Automated Motor Vehicles',
    to: '/companies/:businessEntityId/automated-vehicles',
    feature: CompaniesFeature.AutomatedVehicles,
  },
  {
    label: 'Insurance',
    to: '/companies/:businessEntityId/insurance',
    feature: InsuranceFeature.View,
  },
  {
    label: 'HHG Tariff',
    to: '/companies/:businessEntityId/hhg-tariff',
    feature: CertificatesFeature.HhgTariff,
  },
  {
    label: 'Payments',
    to: '/companies/:businessEntityId/payments',
  },
  {
    label: 'Users',
    to: '/companies/:businessEntityId/users',
    feature: CompaniesFeature.Users,
  },
  {
    label: 'History',
    to: '/companies/:businessEntityId/history',
    feature: CompaniesFeature.History,
  },
] satisfies readonly NavigationItem[]

export default function CompanyDetailsNavigation({
  company,
}: CompanyDetailsNavigationProps) {
  const { id: businessEntityId } = company
  const { user } = useAuth()
  const { cargoTypes } = useCarrierCargoTypes(company.usdotNumber ?? undefined)
  const hasHouseholdGoods = cargoTypes?.houseHoldGoods ?? false

  if (!businessEntityId) return null

  // Insurance does not apply to Broker Certificate holders
  const isBroker = isBrokerCompany(company)

  // Build eligible navigation items
  const navigation = CompanyDetailsNavigationItems.filter(
    ({ feature, to }) =>
      (!feature || feature.isEnabled(user)) &&
      !(isBroker && to === '/companies/:businessEntityId/insurance') &&
      !(to === '/companies/:businessEntityId/hhg-tariff' && !hasHouseholdGoods)
  )

  return (
    <div className="bg-primary text-primary-foreground rounded-md p-4">
      <ScrollArea className="-mb-2 w-full pb-1 whitespace-nowrap">
        <NavigationMenu>
          {navigation.length === 1 ? (
            <div className="pb-1">
              <span className="decoration-primary-foreground whitespace-nowrap underline decoration-2 underline-offset-6">
                {navigation[0].label}
              </span>
            </div>
          ) : (
            <NavigationMenuList className="flex gap-4 lg:gap-6">
              {navigation.map(({ label, to, feature }) =>
                feature ? (
                  <HasFeature key={to} feature={feature}>
                    <CompanyDetailsNavigation.Item
                      to={buildPath(to, { businessEntityId })}
                    >
                      {label}
                    </CompanyDetailsNavigation.Item>
                  </HasFeature>
                ) : (
                  <CompanyDetailsNavigation.Item
                    key={to}
                    to={buildPath(to, { businessEntityId })}
                  >
                    {label}
                  </CompanyDetailsNavigation.Item>
                )
              )}
            </NavigationMenuList>
          )}
        </NavigationMenu>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
}

CompanyDetailsNavigation.Item = function CompanyDetailsNavigationItem({
  to,
  children,
}: PropsWithChildren<{ to: To }>) {
  return (
    // Bottom padding to account for scrollbar height to show underline decoration
    <NavigationMenuItem className="pb-1">
      <ReactRouterNavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'hover:decoration-primary-foreground whitespace-nowrap underline decoration-transparent decoration-2 underline-offset-6 transition-colors duration-200',
            isActive ? 'decoration-primary-foreground' : null
          )
        }
      >
        {children}
      </ReactRouterNavLink>
    </NavigationMenuItem>
  )
}
